import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isAbort, session } from '../../shell/bridge';
import { ConciergeOnButton } from '../Notebook/ConciergeOnButton';
import './digest.css';

/**
 * Partner digest in Us (card g3ixXFWzhgJY).
 *
 * Jonathan's shape, 2026-10-06: a feature inside Us, agent-enabled, built
 * from one partner's interests for the other, 2-4 story summaries at the X
 * times a week each person sets in Settings.
 *
 * Nothing here decides anything — the panel reads state, asks for a build
 * when the window has elapsed, and prints the honest reason when there is
 * none to show. A page load never spends a call by itself: POST is only made
 * when frequency, permission, source interests and the due window all line up.
 */

interface DigestStory {
  title: string;
  summary: string;
  url: string;
  source: string;
}

interface PartnerDigest {
  id: string;
  receiverId: string;
  sourceId: string;
  topics: string[];
  /** The concierge's ~75-word passage; empty on the local fallback. */
  body: string;
  stories: DigestStory[];
  origin: 'model' | 'local';
  createdAt: number;
}

interface DigestState {
  frequency: number;
  digest: PartnerDigest | null;
  due: boolean;
  enabled: boolean;
  sourceTopics: string[];
  partner: { memberId: string; completed: boolean } | null;
}

interface DigestPanelProps {
  members: { memberId: string; displayName: string }[];
}

/** Card wDQm8WPQHj2i — the deep link that opens Settings on the You section,
 *  where "Your interests" lives (settingsTabFromHash in Settings/index.tsx). */
const INTERESTS_HASH = '#settings-you';

const since = (at: number): string => {
  const mins = Math.max(0, Math.round((Date.now() - at) / 60000));
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export function DigestPanel({ members }: DigestPanelProps) {
  const me = session();
  const [state, setState] = useState<DigestState | null>(null);
  const [building, setBuilding] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const attempted = useRef(false);

  const partner = members.find(m => m.memberId !== me?.memberId);
  const partnerName = partner?.displayName ?? 'your partner';

  const load = useCallback(async () => {
    try {
      const data = await api<DigestState>('/api/digest');
      setState({
        frequency: Number(data.frequency) || 0,
        digest: data.digest ?? null,
        due: Boolean(data.due),
        enabled: Boolean(data.enabled),
        sourceTopics: Array.isArray(data.sourceTopics) ? data.sourceTopics : [],
        partner: data.partner ?? null,
      });
    } catch (e) {
      // A page teardown cancels this request; that is not a load failure
      // (card d0Md65M0shSI).
      if (!isAbort(e)) console.error('[Pear] digest load failed', e);
    }
  }, []);

  const build = useCallback(async () => {
    setBuilding(true);
    setReason(null);
    try {
      const data = await api<{ generated: boolean; reason?: string }>('/api/digest/generate', {
        method: 'POST',
        body: {},
      });
      if (!data.generated && data.reason && data.reason !== 'not_due') setReason(data.reason);
    } catch (e) {
      console.error('[Pear] digest build failed', e);
      setReason('failed');
    } finally {
      setBuilding(false);
      await load();
    }
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Card wDQm8WPQHj2i — the CTA out to your own interests profile. The
   *  anchor's href does the opening (App's hashchange handler reads
   *  #settings-you and puts Settings on the You section); this only lands
   *  the view on "Your interests" instead of the top of the page, waiting a
   *  beat for Settings to mount the same way the More menu's quick links do.
   *  A hash that does not change fires no hashchange, so when the link is
   *  already set the event is dispatched by hand. */
  const openOwnInterests = useCallback(() => {
    if (window.location.hash === INTERESTS_HASH) {
      window.dispatchEvent(new Event('hashchange'));
    }
    window.setTimeout(() => {
      document.getElementById('set-interests-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 160);
  }, []);

  // One build per page load, and only when every condition holds.
  useEffect(() => {
    if (!state || attempted.current) return;
    if (state.frequency <= 0 || !state.enabled || !state.due) return;
    if (state.sourceTopics.length === 0) return;
    attempted.current = true;
    void build();
  }, [state, build]);

  if (!state) return null;

  const { frequency, digest, enabled, sourceTopics } = state;

  return (
    <section className="digest" id="digest-panel" aria-labelledby="digest-title">
      <h2
        className="section-title"
        id="digest-title"
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'var(--type-h3-size)',
          letterSpacing: 'var(--type-tracking-wider)',
          fontWeight: 400,
          color: 'var(--color-cream)',
        }}
      >
        Partner digest
      </h2>

      {frequency <= 0 ? (
        <p className="digest-note" id="digest-off">
          Off. Choose how often one arrives in Settings.
        </p>
      ) : !enabled ? (
        /* Card cEuitTMwLs6f Phase 2 — the same dead end the gift surface had.
           The paywall sheet sells the "Partner interest digest" by name; a
           subscriber who follows it here met a pointer to another screen. The
           off state now carries the one-tap (category 'digest') and the
           sentence stays as its note. `onOn` re-reads /api/digest, so the
           panel proceeds in place: `enabled` flips true and the due-window
           effect above is then free to build.
           A div, not a p (sol review 2026-10-15): the one-tap's root is a
           flex span, and any non-phrasing child a future surface adds would
           make the browser close this element early and scatter the copy. */
        <div className="digest-note" id="digest-disabled">
          <p className="digest-off-copy">
            The concierge needs the Partner digest permission before it can write one.
          </p>
          <ConciergeOnButton
            category="digest"
            label="Turn on the digest"
            note="or Settings → Concierge"
            testId="concierge-on-digest"
            onOn={() => void load()}
          />
        </div>
      ) : sourceTopics.length === 0 ? (
        <p className="digest-note" id="digest-needs-interests">
          Nothing to build from yet: this one is written from {partnerName}’s interests.
        </p>
      ) : (
        <>
          <p className="digest-meta" id="digest-meta">
            {frequency}× a week · from {partnerName}’s interests
            {digest ? ` · built ${since(digest.createdAt)}` : ''}
          </p>

          {building && (
            <p className="digest-note" id="digest-building" aria-live="polite">
              Writing your digest…
            </p>
          )}

          {!building && reason === 'news_unavailable' && (
            <p className="digest-note" id="digest-unavailable" aria-live="polite">
              The news isn’t answering right now. It will try again when next due.
            </p>
          )}

          {!building && !reason && !digest && (
            <p className="digest-note" id="digest-empty">
              Nothing here yet — the next one arrives when your window comes round.
            </p>
          )}

          {digest && (
            <div className="digest-stories" id="digest-stories" data-origin={digest.origin}>
              {/* Model origin: the concierge's synthesised passage first, then
                  the stories it cited as references. The standfirsts are never
                  the copy — they were read, not published. */}
              {digest.origin === 'model' && digest.body && (
                <p className="digest-body" id="digest-body">{digest.body}</p>
              )}

              {digest.origin === 'model' ? (
                <div className="digest-sources" id="digest-sources">
                  <span className="digest-sources-label">Built from</span>
                  {digest.stories.map(story => (
                    <a
                      className="digest-source"
                      key={story.url || story.title}
                      href={story.url || undefined}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {story.title}
                      {story.source ? <span className="digest-source-name"> — {story.source}</span> : null}
                    </a>
                  ))}
                </div>
              ) : (
                digest.stories.map(story => (
                  <article className="digest-story" key={`${story.url || story.title}`}>
                    <h3 className="digest-story-title">{story.title}</h3>
                    <p className="digest-story-summary">{story.summary}</p>
                    {story.url ? (
                      <a className="digest-story-link" href={story.url} target="_blank" rel="noreferrer noopener">
                        {story.source || 'Read it'}
                      </a>
                    ) : (
                      <span className="digest-story-link digest-story-source">{story.source}</span>
                    )}
                  </article>
                ))
              )}

              <p className="digest-origin" id="digest-origin">
                {digest.origin === 'model'
                  ? 'Written by the concierge.'
                  : 'Headlines and standfirsts — the concierge didn’t write these ones.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Card wDQm8WPQHj2i (Jonathan email 2026-10-18) — the digest is
          written from your partner's interests, so the half a reader can
          act on from here is their own profile. Present in every state: off,
          closed, waiting and published all keep the way in. */}
      <p className="digest-interests">
        <a
          className="digest-interests-link"
          id="digest-interests-link"
          href={INTERESTS_HASH}
          onClick={openOwnInterests}
        >
          See and update your own interests
        </a>
      </p>
    </section>
  );
}
